import { useState, useCallback, useEffect, useMemo } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { useGetUsersQuery } from "../../../redux/services/userManagementApi";
import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import {
  selectTasksFilters,
  setTasksAssigneeIds,
} from "../../../redux/slices/tasksFilterSlice";
import { useDispatch, useSelector } from "react-redux";

const TaskAssigneeFilter = ({ onClose }: { onClose: () => void }) => {
  const tasksFilters = useSelector(selectTasksFilters);
  const selectedAssignedAgents = tasksFilters?.assigneeIds;

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<
    SearchableDropdownOption[]
  >([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(
    selectedAssignedAgents.value as string[]
  );
  const dispatch = useDispatch();

  const { data: usersData, isLoading, isFetching } = useGetUsersQuery({
    current_page: 1,
    limit: 200,
  });

  const transformedUsers = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (usersData as any)?.user_list?.map((item: any) => ({
        value: item?.user?.id || item?._id,
        label: item?.user?.name || item?.name,
      })) || [],
    [usersData]
  );

  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredUsers(transformedUsers);
        return;
      }
      const filtered = transformedUsers.filter(
        (user: SearchableDropdownOption) =>
          user.label?.toString().toLowerCase().includes(query.toLowerCase())
      );
      setFilteredUsers(filtered);
    },
    [transformedUsers]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  useEffect(() => {
    setFilteredUsers(transformedUsers);
  }, [transformedUsers]);

  const handleUserSelect = (userId: string) => {
    setSelectedUserIds((prevIds) =>
      prevIds.includes(userId)
        ? prevIds.filter((id) => id !== userId)
        : [...prevIds, userId]
    );
  };

  const handleApply = () => {
    const selectedDisplayValues = selectedUserIds
      .map(
        (id) =>
          transformedUsers.find((u: SearchableDropdownOption) => u.value === id)
            ?.label
      )
      .filter(Boolean) as string[];

    dispatch(
      setTasksAssigneeIds({
        value: selectedUserIds,
        displayValue: selectedDisplayValues,
      })
    );
    onClose();
  };

  const isApplyDisabled = () =>
    JSON.stringify([...(selectedUserIds || [])].sort()) ===
    JSON.stringify(
      [...((selectedAssignedAgents?.value as string[]) || [])].sort()
    );

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-70px)]">
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search user"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>
      <div className="overflow-y-auto">
        {selectedUserIds.length > 0 && (
          <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk">
            {selectedUserIds.map((userId) => (
              <div
                key={userId}
                onClick={() => handleUserSelect(userId)}
                className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                    {
                      transformedUsers.find(
                        (u: SearchableDropdownOption) => u.value === userId
                      )?.label
                    }
                  </span>
                </div>
                <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                  <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col">
          <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
            All Users
          </div>
          <div className="overflow-y-auto">
            {isLoading || isFetching ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {isLoading ? "Loading users..." : "Fetching users..."}
                </div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                  {searchQuery.trim()
                    ? "No users found"
                    : "No users available"}
                </div>
              </div>
            ) : (
              filteredUsers
                ?.filter(
                  (user: SearchableDropdownOption) =>
                    !selectedUserIds.includes(user.value)
                )
                ?.map((user: SearchableDropdownOption) => (
                  <div
                    key={user.value}
                    onClick={() => handleUserSelect(user.value)}
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                        {user.label}
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                      <div className="w-3 h-3 rounded-sm bg-transparent" />
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6 px-3">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled()}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default TaskAssigneeFilter;
